/*
Copyright (c) 2012-2022 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import moment, { Moment } from "moment-timezone"
import { DAILY_SELECTION_LIMIT } from "../constants/misc"

export const isParsedUTCDateValid = (
  date: Moment,
  timezone?: string,
  dateRange?: number,
): boolean => {
  if (!date.isValid()) {
    return false
  }
  const utcDate = date.isUTC()
    ? date
    : date.clone().utc()
  const todayUTC = (timezone
    ? moment.tz(timezone)
    : moment()
  )
    .endOf("day")
    .utc()
  return (
    utcDate.isBefore(todayUTC) &&
    utcDate.isAfter(
      todayUTC.subtract(
        dateRange ?? DAILY_SELECTION_LIMIT,
        "day",
      ),
    )
  )
}

export const nextUTCDate = (
  utcDate: Moment,
  num: number = 1,
): Moment => {
  const currentUTC = moment.utc()
  const nextDate = utcDate.clone().add(num, "day")
  if (nextDate.isAfter(currentUTC)) {
    return currentUTC
  }
  return nextDate
}

/**
 *
 * @param timestamp 1571765356000("2019-10-22T17:29:16Z")
 * @param timezone "Asia/Tokyo"
 * @param format "DD/MM/YYYY HH:mm"
 * @returns "23/10/2019 02:29"("2019-10-23T02:29:16+09:00")
 */
export const timestampToLocalDateTime = (
  timestamp: Timestamp,
  timezone: string,
  format: string,
): string => {
  let localDateTime = moment(timestamp).tz(
    timezone,
  )
  if (!localDateTime) {
    localDateTime = moment(timestamp)
  }
  return localDateTime.format(format)
}

/**
 *
 * @param utcDateTime  1571736600000 === "2019-10-22T09:30:00Z" === "2019-10-22T17:30:00+08:00"
 * @param targetTimezone   "Asia/Tokyo"
 * @returns "2019-10-22T08:30:00Z" === "2019-10-22T17:30:00+09:00"
 */
export const dateTimeRebasedUTC = (
  timestamp: Timestamp,
  targetTimezone: string,
  asString: boolean = true,
  originalTimezone: string | null = null,
  format: string = "YYYY-MM-DD HH:mm:ss",
): string | Moment => {
  let targetDateTimeStr
  if (originalTimezone === null) {
    targetDateTimeStr = moment(timestamp).format(
      format,
    )
  } else {
    targetDateTimeStr = moment
      .tz(timestamp, originalTimezone)
      .format(format)
  }
  const result = moment
    .tz(targetDateTimeStr, format, targetTimezone)
    .utc()
  if (asString) {
    return result.format()
  }
  return result
}

/**
 *
 * @param timestamp
 * @param timezone
 */
export const timestampRebaseToBrowserTime = (
  timestamp: Timestamp | undefined | null,
  timezone: string,
): Moment => {
  if (!timestamp || Number.isNaN(timestamp)) {
    return moment().startOf("day")
  }
  const result = dateTimeRebasedUTC(
    timestamp,
    moment.tz.guess(),
    false,
    timezone,
  ) as Moment
  if (isParsedUTCDateValid(result)) {
    return result.local()
  }
  return moment().startOf("day")
}

export const isMidnight = (
  date: Moment,
  timezone?: string,
) => {
  const target = timezone
    ? date.clone().tz(timezone)
    : date.clone().local()
  return (
    target.get("hours") === 0 &&
    target.get("minutes") === 0 &&
    target.get("seconds") === 0
  )
}

export const howManyDaysPassed = (
  startDateTimestamp: Timestamp,
  endDateTimestamp: Timestamp,
): number =>
  Math.ceil(
    (endDateTimestamp - startDateTimestamp) /
      (3600 * 24 * 1000),
  )

/*
There are some timezones which have uneven UTC offsets that differ by 30 or 45 minutes. (e.g Asia/Yangon)
This function is used to return the minutes remainder(30 or 45)
Currently only used in trx
*/
export const timezoneOffsetMinuteRemainder = (
  timezone?: string,
): number => {
  if (timezone == null) return 0
  const utcOffset = moment
    .tz(timezone)
    .utcOffset()
  let count = 0
  while (count * 60 < utcOffset) count += 1
  if (utcOffset === count * 60) return 0
  return utcOffset - (count - 1) * 60
}
